/**
 * Shared org-API fetch helper for the YaaS Client Console services.
 *
 * Wraps `orgAuthFetch` with parsed-JSON return and a single error-shape
 * parser. Replaces four near-identical `readJsonResponse` / `readErrorMessage`
 * blocks that previously lived in clientPositions /
 * clientSavedDestinations / clientDashboard.
 */

import { CLIENT_OPERATING_CONTEXT_HEADER, orgAuthFetch, useOrgAuthStore } from "@solomon-apps/shared";

import { CLIENT_OAUTH_CONFIG } from "@/constants/oauthConfig";

import { mockApiFetch } from "./sim/mockApi";
import { isSimMode } from "./sim/simMode";

interface ErrorResponse {
    error?: string;
    message?: string;
    code?: string;
}

interface ParsedErrorResponse {
    message: string;
    code?: string;
}

export class OrgApiError extends Error {
    constructor(
        message: string,
        readonly status: number,
        readonly code?: string
    ) {
        super(message);
        this.name = "OrgApiError";
    }
}

async function readErrorResponse(response: Response): Promise<ParsedErrorResponse> {
    const body = (await response.json().catch((): null => null)) as ErrorResponse | null;
    return {
        message: body?.message ?? body?.error ?? `Request failed: ${response.status}`,
        ...(typeof body?.code === "string" ? { code: body.code } : {})
    };
}

function withSelectedClientContextHeader(init?: RequestInit): RequestInit | undefined {
    const selectedContextUuid = useOrgAuthStore.getState().selectedClientContextUuid;
    if (!selectedContextUuid) {
        return init;
    }

    const headers = new Headers(init?.headers);
    if (!headers.has(CLIENT_OPERATING_CONTEXT_HEADER)) {
        headers.set(CLIENT_OPERATING_CONTEXT_HEADER, selectedContextUuid);
    }

    return {
        ...init,
        headers
    };
}

/**
 * Fetch a path on the client API. The `path` is appended to the OAuth
 * config's `apiBase`; pass a string starting with `/` (e.g. `/api/org/...`)
 * or a fully-qualified URL.
 *
 * Returns the response's JSON body parsed as `T`, or throws an
 * `OrgApiError` with the API's `message`/`error` and stable `code` field
 * when present.
 */
export async function orgApiFetch<T>(path: string, init?: RequestInit): Promise<T> {
    if (isSimMode()) {
        const mock = await mockApiFetch(init?.method ?? "GET", path, init);
        if (mock.hit) return mock.value as T;
    }
    const url = path.startsWith("http") ? path : `${CLIENT_OAUTH_CONFIG.apiBase}${path}`;
    const response = await orgAuthFetch(url, withSelectedClientContextHeader(init), CLIENT_OAUTH_CONFIG);
    if (!response.ok) {
        const error = await readErrorResponse(response);
        throw new OrgApiError(error.message, response.status, error.code);
    }
    return response.json() as Promise<T>;
}

export async function orgApiFetchResponse(path: string, init?: RequestInit): Promise<Response> {
    if (isSimMode()) {
        const mock = await mockApiFetch(init?.method ?? "GET", path, init);
        if (mock.hit) {
            return new Response(JSON.stringify(mock.value), {
                status: 200,
                headers: { "Content-Type": "application/json", ETag: '"simulated-capability-state"' }
            });
        }
    }
    const url = path.startsWith("http") ? path : `${CLIENT_OAUTH_CONFIG.apiBase}${path}`;
    const response = await orgAuthFetch(url, withSelectedClientContextHeader(init), CLIENT_OAUTH_CONFIG);
    if (!response.ok) {
        const error = await readErrorResponse(response);
        throw new OrgApiError(error.message, response.status, error.code);
    }
    return response;
}

/**
 * Variant for endpoints that return no body (e.g. DELETE). Throws on
 * non-2xx; otherwise resolves without a value.
 */
export async function orgApiFetchVoid(path: string, init?: RequestInit): Promise<void> {
    if (isSimMode()) {
        const mock = await mockApiFetch(init?.method ?? "GET", path, init);
        if (mock.hit) return;
    }
    const url = path.startsWith("http") ? path : `${CLIENT_OAUTH_CONFIG.apiBase}${path}`;
    const response = await orgAuthFetch(url, withSelectedClientContextHeader(init), CLIENT_OAUTH_CONFIG);
    if (!response.ok) {
        const error = await readErrorResponse(response);
        throw new OrgApiError(error.message, response.status, error.code);
    }
}

/** Standard paginated wrapper used by several list endpoints. */
export interface PaginatedResponse<T> {
    data: T[];
    total: number;
    limit: number;
    offset: number;
    page: number;
}
